import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../store';
import { logout } from '../store/slices/authSlice';
import { getApiUrl } from '../config/api.config';

const baseQuery = fetchBaseQuery({
  baseUrl: '', // Empty base URL, we'll provide full URLs
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.accessToken;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

// Custom base query that dynamically determines API URL
const dynamicBaseQuery = async (args: any, api: any, extraOptions: any) => {
  // Construct full URL with current API base
  const apiBase = getApiUrl();
  console.log('🔍 DynamicBaseQuery:', { apiBase, hostname: window.location.hostname, originalArgs: args });
  
  if (typeof args === 'string') {
    args = { url: `${apiBase}${args}` };
  } else if (args.url) {
    args = { ...args, url: `${apiBase}${args.url}` };
  }
  
  console.log('🔍 Final request args:', args);
  return baseQuery(args, api, extraOptions);
};

// Track ongoing refresh requests to prevent multiple simultaneous refresh attempts
let refreshPromise: Promise<any> | null = null;
let lastRefreshTime = 0;
let refreshAttempts = 0;
const REFRESH_COOLDOWN = 5000; // 5 seconds cooldown between refreshes
const MAX_REFRESH_ATTEMPTS = 3; // Maximum refresh attempts before forcing logout
const REFRESH_ATTEMPT_WINDOW = 60000; // 1 minute window for tracking attempts

// Reset refresh attempts counter periodically
const resetRefreshAttempts = () => {
  const currentTime = Date.now();
  if (currentTime - lastRefreshTime > REFRESH_ATTEMPT_WINDOW) {
    refreshAttempts = 0;
  }
};

const forceLogout = (api: any, reason: string) => {
  console.warn(`Forcing logout: ${reason}`);
  // Clear all refresh tracking
  refreshPromise = null;
  refreshAttempts = 0;
  lastRefreshTime = 0;
  // Dispatch logout
  api.dispatch(logout());
};

const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  let result = await dynamicBaseQuery(args, api, extraOptions);
  
  if (result.error && result.error.status === 401) {
    const state = api.getState() as RootState;
    const refreshToken = state.auth.refreshToken;
    const currentTime = Date.now();
    
    // Reset attempts counter if enough time has passed
    resetRefreshAttempts();
    
    // Check if we've exceeded maximum refresh attempts
    if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
      forceLogout(api, `Maximum refresh attempts (${MAX_REFRESH_ATTEMPTS}) exceeded`);
      return result;
    }
    
    // Check if we have a valid refresh token
    if (!refreshToken) {
      forceLogout(api, 'No refresh token available');
      return result;
    }
    
    // Check cooldown period
    if (currentTime - lastRefreshTime <= REFRESH_COOLDOWN) {
      console.warn('Refresh attempted too frequently, skipping');
      return result;
    }
    
    // If there's an ongoing refresh, wait for it
    if (refreshPromise) {
      try {
        const refreshResult = await refreshPromise;
        if (refreshResult && refreshResult.data) {
          // Retry original request after successful refresh
          result = await dynamicBaseQuery(args, api, extraOptions);
        } else {
          forceLogout(api, 'Ongoing refresh failed');
        }
        return result;
      } catch (error) {
        forceLogout(api, 'Error waiting for ongoing refresh');
        return result;
      }
    }
    
    // Start new refresh process
    refreshAttempts++;
    refreshPromise = Promise.resolve(dynamicBaseQuery(
      {
        url: '/auth/refresh',
        method: 'POST',
        body: { refreshToken },
      },
      api,
      extraOptions
    ));
    
    try {
      const refreshResult = await refreshPromise;
      
      if (refreshResult.data?.data) {
        // Token refrescado exitosamente
        const { accessToken, refreshToken: newRefreshToken, user } = refreshResult.data.data;
        
        lastRefreshTime = currentTime;
        refreshAttempts = 0; // Reset attempts on success
        
        // Actualizar tokens en el store
        api.dispatch({
          type: 'auth/setCredentials',
          payload: {
            user: user || state.auth.user,
            accessToken,
            refreshToken: newRefreshToken,
          },
        });
        
        // Reintentar la petición original con el nuevo token
        result = await dynamicBaseQuery(args, api, extraOptions);
        
        console.log('✅ Token refreshed successfully');
      } else {
        // El refresh falló pero no hubo error de red
        forceLogout(api, 'Refresh response invalid or empty');
      }
    } catch (error) {
      // Error en el refresh (red, servidor, etc.)
      console.error('❌ Refresh error:', error);
      forceLogout(api, `Refresh request failed: ${error}`);
    } finally {
      // Reset refresh promise
      refreshPromise = null;
    }
  }
  
  return result;
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Product', 'Inventory', 'Sale', 'Location', 'User', 'Transfer', 'TransferDraft', 'Commission', 'Payroll', 'Advance', 'CashDrawer', 'Report', 'AuditLog', 'BulkAdjustments'],
  endpoints: () => ({}),
});